import React, { useEffect, useState } from 'react';
import { recordingsApi } from '../../api';
import { CheckIcon, AlertTriangleIcon } from '../icons';

/**
 * Detail view for a completed background recording job — QR code, watch
 * link, per-recipient email status, retry. Opened by tapping "View" on a
 * finished BackgroundJobsBar pill; unlike the old DoneScreen this is a
 * dismissible overlay, not a full-page screen the kiosk is stuck on — the
 * kiosk is already back on the recording screen underneath.
 */
export default function RecordingDetailModal({ session, courtName, onClose }) {
  const [results, setResults] = useState(session.email_results || {});
  const [retrying, setRetrying] = useState(false);
  const [retryMsg, setRetryMsg] = useState('');

  const recipients = session.recipient_emails || [session.player_email];
  const failed = recipients.filter(addr => results[addr] && !results[addr].success);
  const pending = recipients.filter(addr => !results[addr]);

  useEffect(() => {
    if (pending.length === 0) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const latest = await recordingsApi.get(session.id);
        if (!cancelled) setResults(latest.email_results || {});
      } catch { /* ignore, retry on next tick */ }
    };
    const id = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [pending.length, session.id]);

  const handleRetry = async () => {
    setRetrying(true);
    setRetryMsg('');
    try {
      const { summary, session: updated } = await recordingsApi.resendEmail(session.id);
      setResults(updated.email_results || {});
      if (summary.failed.length === 0 && summary.sent.length > 0) {
        setRetryMsg(`Sent! ${summary.sent.length} email(s) delivered.`);
      } else if (summary.sent.length === 0 && summary.failed.length === 0) {
        setRetryMsg('Nothing to retry.');
      } else {
        setRetryMsg(`${summary.sent.length} sent, ${summary.failed.length} still failing.`);
      }
    } catch {
      setRetryMsg('Retry failed — check your connection and try again.');
    } finally {
      setRetrying(false);
    }
  };

  const qrUrl = recordingsApi.qrCodeUrl(session.id);
  const allGood = failed.length === 0 && pending.length === 0;

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.card} onClick={e => e.stopPropagation()} className="fade-in">
        <button style={s.closeX} onClick={onClose} aria-label="Close">✕</button>

        <div style={{ ...s.check, ...(allGood ? {} : s.checkWarn) }}>
          {allGood ? <CheckIcon size={30} color="var(--green)" /> : <AlertTriangleIcon size={28} color="#d97706" />}
        </div>
        <h1 style={s.title}>{allGood ? 'Recording Sent!' : 'Recording Saved'}</h1>
        <p style={s.subtitle}>
          {allGood
            ? <>Your video is on its way to {recipients.length} recipient{recipients.length > 1 ? 's' : ''}.</>
            : <>Your video is saved — some emails need attention below.</>}
        </p>

        <div style={s.qrWrap}>
          <img src={qrUrl} alt="Scan to open your video" style={s.qrImg} />
          <p style={s.qrCaption}>Scan to open your video on your phone</p>
          {session.watch_url && (
            <a href={session.watch_url} target="_blank" rel="noreferrer" style={s.watchLink}>
              Watch or Download
            </a>
          )}
        </div>

        <div style={s.stats}>
          <div style={s.stat}>
            <div style={s.statVal}>{session.duration_display}</div>
            <div style={s.statLabel}>Duration</div>
          </div>
          <div style={s.divider} />
          <div style={s.stat}>
            <div style={s.statVal}>{courtName}</div>
            <div style={s.statLabel}>Court</div>
          </div>
        </div>

        <div style={s.recipientList}>
          {recipients.map(addr => {
            const r = results[addr];
            const state = !r ? 'pending' : r.success ? 'sent' : 'failed';
            return (
              <div key={addr} style={s.recipientRow}>
                <span style={{
                  ...s.recipientDot,
                  background: state === 'sent' ? 'var(--green)' : state === 'failed' ? 'var(--red)' : 'var(--gray-300)',
                }} />
                <span style={s.recipientEmail}>{addr}</span>
                <span style={s.recipientStatus}>
                  {state === 'sent' ? 'Sent' : state === 'failed' ? 'Failed' : 'Sending…'}
                </span>
              </div>
            );
          })}
        </div>

        {failed.length > 0 && (
          <button onClick={handleRetry} disabled={retrying} style={s.retryBtn}>
            {retrying ? 'Retrying…' : `Retry ${failed.length} failed email${failed.length > 1 ? 's' : ''}`}
          </button>
        )}
        {retryMsg && <p style={s.retryMsg}>{retryMsg}</p>}

        <p style={s.hint}>Links are valid for 7 days.</p>
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 400,
    background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  card: {
    position: 'relative',
    background: 'var(--white)', borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)', padding: '44px 40px',
    width: '100%', maxWidth: 460, textAlign: 'center',
    maxHeight: '90vh', overflowY: 'auto',
  },
  closeX: {
    position: 'absolute', top: 16, right: 16,
    background: 'var(--gray-100)', border: 'none',
    borderRadius: 'var(--radius-sm)', width: 30, height: 30,
    fontSize: 14, cursor: 'pointer', color: 'var(--gray-600)',
  },
  check: {
    width: 64, height: 64, borderRadius: '50%',
    background: 'var(--green-light)', color: 'var(--green)',
    fontSize: 28, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px',
  },
  checkWarn: { background: '#fef3c7', color: '#d97706' },
  title: {
    fontSize: 26, fontWeight: 700, color: 'var(--gray-900)',
    letterSpacing: '-0.5px', marginBottom: 8,
  },
  subtitle: {
    fontSize: 14, color: 'var(--gray-500)', lineHeight: 1.6, marginBottom: 24,
  },
  qrWrap: { marginBottom: 24 },
  qrImg: {
    width: 140, height: 140, borderRadius: 12,
    border: '1px solid var(--gray-200)', padding: 6,
  },
  qrCaption: { fontSize: 12, color: 'var(--gray-400)', marginTop: 8 },
  watchLink: {
    display: 'inline-block', marginTop: 10, fontSize: 13, fontWeight: 600,
    color: 'var(--blue)', textDecoration: 'none',
  },
  stats: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--gray-50)', borderRadius: 'var(--radius)',
    padding: '16px 28px', gap: 28, marginBottom: 20,
  },
  stat: { textAlign: 'center' },
  statVal: { fontSize: 18, fontWeight: 700, color: 'var(--gray-900)', marginBottom: 4 },
  statLabel: { fontSize: 11, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 },
  divider: { width: 1, height: 32, background: 'var(--gray-200)' },
  recipientList: {
    textAlign: 'left', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius)',
    padding: '4px 16px', marginBottom: 20,
  },
  recipientRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 0', borderBottom: '1px solid var(--gray-100)',
  },
  recipientDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  recipientEmail: {
    flex: 1, fontSize: 13, color: 'var(--gray-700)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  recipientStatus: { fontSize: 12, fontWeight: 600, color: 'var(--gray-400)' },
  retryBtn: {
    width: '100%', background: '#fef3c7', color: '#92400e',
    border: '1px solid #fde68a', borderRadius: 'var(--radius-sm)',
    padding: '12px', fontSize: 14, fontWeight: 600,
    cursor: 'pointer', marginBottom: 12,
  },
  retryMsg: { fontSize: 13, color: 'var(--gray-500)', marginBottom: 12 },
  hint: {
    fontSize: 12, color: 'var(--gray-400)',
    lineHeight: 1.6,
  },
};
